import { Skeleton, TextField } from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField";
import { forwardRef, useId } from "react";

import { hiddenUnderlineSx } from "@themes/utils/hiddenUnderlineSx";
import { BaseFormLabel } from "@ui/atoms/BaseFormLabel/BaseFormLabel";

export type BaseTextFieldProps = TextFieldProps & {
  loading?: boolean;
  disabled: boolean;
  labelText?: string;
  skeletonHeight?: string;
  maxLength?: number;
  isRequired?: boolean;
  tooltipText?: string | React.ReactNode;
  dataTestId?: string;
  onSubmit?: () => void;
};

// Backwards-compatible alias for the previous internal name.
export type Props = BaseTextFieldProps;

/**
 * Pure builder for the merged `slotProps` passed to the underlying TextField.
 * `disableUnderline` is stripped from the consumer's input slot (it is applied
 * via sx instead, see {@link buildUnderlineSx}).
 */
export const buildSlotProps = (
  slotProps: TextFieldProps["slotProps"],
  {
    dataTestId,
    inputId,
    maxLength,
    name
  }: {
    dataTestId?: string;
    inputId: string;
    maxLength?: number;
    name?: string;
  }
) => {
  const { disableUnderline: _disableUnderline, ...filteredInputSlotProps } =
    (slotProps?.input as Record<string, unknown>) || {};
  void _disableUnderline;
  return {
    ...slotProps,
    htmlInput: {
      ...slotProps?.htmlInput,
      "data-test-id": dataTestId,
      id: inputId,
      maxLength,
      name
    },
    input: filteredInputSlotProps
  };
};

/**
 * Pure builder for the field's `sx`. When the consumer's input slot requested
 * `disableUnderline`, the shared underline-hiding fragment is merged in.
 */
export const buildUnderlineSx = (
  slotProps: TextFieldProps["slotProps"],
  sx: TextFieldProps["sx"]
): TextFieldProps["sx"] => {
  const { disableUnderline } =
    (slotProps?.input as Record<string, unknown>) || {};
  return disableUnderline
    ? {
        ...hiddenUnderlineSx,
        ...(sx && typeof sx === "object" ? sx : {})
      }
    : sx;
};

export const BaseTextField = forwardRef<HTMLInputElement, Props>(
  (props, ref) => {
    const {
      dataTestId,
      disabled,
      fullWidth,
      isRequired,
      labelText,
      loading = false,
      maxLength,
      multiline,
      name,
      onBlur,
      onChange,
      onFocus: onFocusProp,
      onSubmit,
      skeletonHeight,
      tooltipText,
      value,
      ...otherProps
    } = props;

    const { slotProps, sx, ...restProps } = otherProps;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !multiline) {
        e.preventDefault();
        // ponytail: onBlur cast kept as-is — many callers pass onBlur+onSubmit
        // together; the refactor note flags this cast as high behavioural risk
        // and not safely unit-verifiable, so it is intentionally not rewritten.
        onBlur?.(e as unknown as React.FocusEvent<HTMLInputElement>);
        (e.target as HTMLInputElement).blur();
        onSubmit?.();
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      onFocusProp?.(e);
      e.target.select();
    };

    const generatedId = useId();
    const inputId = name ?? generatedId;

    const combinedSx = buildUnderlineSx(slotProps, sx);
    const mergedSlotProps = buildSlotProps(slotProps, {
      dataTestId,
      inputId,
      maxLength,
      name
    });

    return (
      <>
        {labelText ? (
          <BaseFormLabel
            loading={loading}
            component="label"
            htmlFor={inputId}
            tooltipText={tooltipText}
            required={isRequired}
          >
            {labelText}
          </BaseFormLabel>
        ) : null}

        {loading ? (
          <Skeleton
            variant="rounded"
            width={fullWidth ? "100%" : undefined}
            height={skeletonHeight ?? 40}
            sx={{
              borderRadius: 2
            }}
          />
        ) : (
          <TextField
            {...restProps}
            ref={ref}
            disabled={disabled}
            fullWidth={fullWidth}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            multiline={multiline}
            slotProps={mergedSlotProps}
            sx={combinedSx}
          />
        )}
      </>
    );
  }
);

BaseTextField.displayName = "BaseTextField";
